/**
 * HVP Variation Packer (client-side decoder)
 *
 * Decodes the compact, dictionary-indexed `variationsPacked` payload back into
 * the canonical full variation array the cascade expects:
 *   [{ variation_id, attributes, is_purchasable, is_in_stock, has_price,
 *      is_valid_for_purchase, price, price_html, image, sku }, ...]
 *
 * This MUST mirror src/Frontend/VariationEncoder.php::decode() exactly. The PHP
 * side runs a lossless self-check (decode + field-for-field compare) every render
 * and only ships `variationsPacked` when the round-trip is byte-exact; otherwise
 * it ships the full `allVariations` array, so this decoder is always fed a payload
 * that reconstructs the identical objects.
 *
 * Pure: no DOM, no globals, no side effects.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

/**
 * @param {Object} packed - The variationsPacked structure from hvpData.
 * @returns {Array} Full variation objects.
 */
export function decodePackedVariations(packed) {
    if (!packed || !Array.isArray(packed.rows)) {
        return [];
    }

    const attrKeys = Array.isArray(packed.attrKeys)
        ? packed.attrKeys
        : Object.keys(packed.attrDict || {});
    const attrDict = packed.attrDict || {};
    const priceDict = packed.priceDict || [];
    const rows = packed.rows;
    const mode = packed.priceMode;
    const nums = packed.priceHtmlNum || [];
    const tpl = packed.priceTpl || {};
    const prefix = tpl.prefix || '';
    const suffix = tpl.suffix || '';
    const htmlDict = packed.priceHtmlDict || [];
    const htmlRefs = packed.htmlRefs || [];
    const keyCount = attrKeys.length;

    const out = new Array(rows.length);

    for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        const id = row[0];
        const idx = row[1] || [];
        const pRef = (row[2] === undefined || row[2] === null) ? -1 : row[2];
        const flags = row[3] || 0;
        const sku = (row[4] === undefined || row[4] === null) ? '' : row[4];

        const attributes = {};
        for (let i = 0; i < keyCount; i++) {
            const vi = idx[i];
            if (vi === -1 || vi === undefined || vi === null) {
                continue;
            }
            const key = attrKeys[i];
            const vals = attrDict[key];
            if (vals && vi >= 0 && vi < vals.length) {
                attributes[key] = vals[vi];
            }
        }

        const price = (pRef >= 0 && pRef < priceDict.length) ? priceDict[pRef] : 0;
        const isPurchasable = (flags & 1) === 1;
        const inStock = (flags & 2) === 2;

        let priceHtml = '';
        if (mode === 'reconstruct') {
            if (price > 0) {
                const mid = (pRef >= 0 && pRef < nums.length) ? nums[pRef] : '';
                priceHtml = prefix + mid + suffix;
            }
        } else {
            const h = htmlRefs[r];
            priceHtml = (h !== undefined && h >= 0 && h < htmlDict.length) ? htmlDict[h] : '';
        }

        out[r] = {
            variation_id: id,
            // Mirror PHP decode()/getAllVariations() exactly: an attribute-less row
            // is an empty PHP array (json []), not an object. Emit [] when no keys
            // were set so the shape is byte-identical to the legacy allVariations path.
            attributes: (Object.keys(attributes).length === 0 ? [] : attributes),
            is_purchasable: isPurchasable,
            is_in_stock: inStock,
            has_price: price > 0,
            is_valid_for_purchase: isPurchasable && price > 0,
            price: price,
            price_html: priceHtml,
            image: [],
            sku: sku
        };
    }

    return out;
}

export default decodePackedVariations;
